<script setup lang="ts">
interface Props {
  productQuantity?: number;
  item?: Record<string, unknown>;
  modifiers?: unknown[];
  minQuantity?: number;
  maxQuantity?: number;
  disabled?: boolean;
  showBin?: boolean;
  disableMinus?: boolean;
  // Blocks every increase path (icon, typed input, picker) while leaving decrease and
  // remove working. Used for a cart line that is no longer orderable: the customer must
  // be able to get rid of it, but must never be able to order more of it.
  disablePlus?: boolean;
  picker?: boolean;
  input?: boolean;
  vertical?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  productQuantity: 1,
  item: () => ({}),
  modifiers: () => [],
  minQuantity: 1,
  maxQuantity: 100,
  disabled: false,
  showBin: true,
  disableMinus: false,
  disablePlus: false,
  picker: false,
  input: false,
  vertical: false,
});

const emit = defineEmits<{
  returnQuantity: [payload: { product: Record<string, unknown>; appliedModifiers: unknown[] }];
}>();

const quantity = ref(0);
const inputQuantity = ref<number | false>(false);
const showPicker = ref(false);
const scrollerOptionsList = ref<number[]>([]);
const quantityBtnRef = ref<HTMLElement | null>(null);
const pickerRef = ref<HTMLElement | null>(null);
const pickerStyle = ref<Record<string, string>>({
  position: "fixed",
  opacity: "0",
  pointerEvents: "none",
  zIndex: "10000",
});

// The picker's selectable quantities. Capped at the current quantity when increases are
// blocked, so the picker can't do what the plus icon and the input can't.
function buildScrollerOptions(currentQuantity: number) {
  const options = [...Array(props.maxQuantity + currentQuantity).keys()].map(
    (i) => i + (props.minQuantity || 1),
  );
  return props.disablePlus ? options.filter((option) => option <= currentQuantity) : options;
}

const isMax = computed(() => {
  if (props.disablePlus) return true;
  return props.maxQuantity <= 0 || (props.maxQuantity < props.minQuantity && quantity.value === 0);
});

// Grow the input with the digit count (1.5rem font) so the whole number stays
// visible: ~2.5rem for 1 digit up to ~5.5rem for 4, stepping by 1rem each.
const inputWidth = computed(() => {
  const digits = String(inputQuantity.value ?? "").replace(/\D/g, "").length || 1;
  return `${digits + 1.5}rem`;
});

const isMin = computed(() => {
  return quantity.value <= props.minQuantity;
});

watch(
  () => props.productQuantity,
  (to) => {
    quantity.value = parseInt(String(to));
    inputQuantity.value = parseInt(String(to));
  },
);

watch(quantity, (to) => {
  quantity.value = parseInt(String(to));
  inputQuantity.value = parseInt(String(to));
});

onMounted(() => {
  quantity.value = props.productQuantity;
  inputQuantity.value = props.productQuantity;
  scrollerOptionsList.value = buildScrollerOptions(props.productQuantity);
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
});

function handleClickOutside(e: MouseEvent) {
  if (!showPicker.value) return;
  const target = e.target as HTMLElement;
  if (quantityBtnRef.value?.contains(target)) return;
  if (pickerRef.value?.contains(target)) return;
  handleScrollerQuantityUpdate();
}

function updatePickerPosition() {
  if (!quantityBtnRef.value) return;
  const rect = quantityBtnRef.value.getBoundingClientRect();
  const pickerHeight = 220; // approximate max height of picker
  const spaceBelow = window.innerHeight - rect.bottom;
  const spaceAbove = rect.top;
  const openAbove = spaceBelow < pickerHeight && spaceAbove > spaceBelow;

  pickerStyle.value = {
    position: "fixed",
    left: `${rect.left + rect.width / 2}px`,
    transform: "translateX(-50%)",
    zIndex: "10000",
    opacity: "1",
    pointerEvents: "auto",
    transition: "opacity 0.15s ease",
    ...(openAbove
      ? { bottom: `${window.innerHeight - rect.top + 4}px`, top: "auto" }
      : { top: `${rect.bottom + 4}px`, bottom: "auto" }),
  };
}

function handleIconOperatorQuantityUpdate(plus: boolean) {
  const itemObj = props.item as Record<string, unknown>;
  if (itemObj?.modifierGroups && (itemObj.modifierGroups as unknown[]).length > 0) {
    emit("returnQuantity", {
      product: { ...itemObj, quantity: quantity.value },
      appliedModifiers: props.modifiers,
    });
  } else {
    if (!plus) {
      quantity.value -= 1;
    } else {
      if (quantity.value >= props.minQuantity) {
        quantity.value += 1;
      } else {
        quantity.value = props.minQuantity;
      }
    }
    emit("returnQuantity", {
      product: { ...itemObj, quantity: quantity.value },
      appliedModifiers: props.modifiers,
    });
    inputQuantity.value = quantity.value;
  }
}

let inputTimeout: ReturnType<typeof setTimeout> | null = null;

function handleInputFieldQuantityUpdate() {
  if (inputTimeout) clearTimeout(inputTimeout);
  inputTimeout = setTimeout(() => {
    // The number input can raise the quantity without going through the plus icon, so the
    // no-increase rule has to be enforced here too — clamp back to what's already on the
    // line rather than rejecting the edit outright.
    if (props.disablePlus && (inputQuantity.value as number) > quantity.value) {
      inputQuantity.value = quantity.value;
    }
    if (!inputQuantity.value) quantity.value = props.minQuantity;
    if (props.minQuantity > 0 && (inputQuantity.value as number) < props.minQuantity) {
      inputQuantity.value = props.minQuantity;
    }
    quantity.value = inputQuantity.value as number;
    const itemObj = props.item as Record<string, unknown>;
    emit("returnQuantity", {
      product: { ...itemObj, quantity: parseInt(String(quantity.value)) },
      appliedModifiers: props.modifiers,
    });
  }, 2000);
}

function handleScrollerQuantityUpdate() {
  showPicker.value = false;
  const itemObj = props.item as Record<string, unknown>;
  emit("returnQuantity", {
    product: { ...itemObj, quantity: quantity.value },
    appliedModifiers: props.modifiers,
  });
  inputQuantity.value = quantity.value;
}

function toggleScroller() {
  if (!props.picker) return;
  if (props.disabled) return;
  showPicker.value = !showPicker.value;

  if (showPicker.value) {
    scrollerOptionsList.value = buildScrollerOptions(quantity.value);
    nextTick(() => {
      updatePickerPosition();
      // Scroll to the currently selected quantity
      const selected = pickerRef.value?.querySelector(".selected-time") as HTMLElement | null;
      if (selected) {
        selected.scrollIntoView({ block: "center" });
      }
    });
  } else {
    handleScrollerQuantityUpdate();
  }
}
</script>

<template>
  <div class="quantity-operator">
    <div
      class="quantity-operator__actions"
      :style="vertical ? 'flex-direction: column-reverse' : ''"
    >
      <fa
        v-if="quantity > minQuantity || quantity > 0"
        class="icon"
        :icon="['fal', isMin && showBin ? 'trash' : 'circle-minus']"
        :class="(disabled || (!showBin && isMin) || disableMinus) && 'disable-icon'"
        @click="
          !disabled &&
          !disableMinus &&
          (showBin || quantity > minQuantity) &&
          handleIconOperatorQuantityUpdate(false)
        "
      />
      <h3
        v-if="(quantity > minQuantity || quantity > 0) && !input"
        ref="quantityBtnRef"
        class="quantity-operator__actions--quantity"
        :class="picker && 'drop-quantity'"
        @click="toggleScroller"
      >
        {{ quantity }}
        <fa
          v-if="picker"
          :icon="['fal', `chevron-${showPicker ? 'up' : 'down'}`]"
          class="grey-text small-icon"
        />
      </h3>
      <input
        v-if="(quantity > minQuantity || quantity > 0) && input"
        v-model="inputQuantity"
        type="number"
        :style="{ width: inputWidth }"
        @input="handleInputFieldQuantityUpdate"
      />
      <fa
        class="icon"
        :icon="['fal', 'circle-plus']"
        :class="(isMax || disabled) && 'disable-icon'"
        @click="!isMax && !disabled && handleIconOperatorQuantityUpdate(true)"
      />
    </div>
    <Teleport to="body">
      <div
        v-if="!disabled && showPicker && picker && !input"
        ref="pickerRef"
        class="quantity-operator__picker"
        :style="pickerStyle"
      >
        <div class="quantity-operator__picker--numbers">
          <div
            v-for="option in scrollerOptionsList"
            :key="option"
            class="slots"
            :class="option === quantity && 'selected-time'"
            @click="
              quantity = option;
              handleScrollerQuantityUpdate();
            "
          >
            <p>{{ option }}</p>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style lang="scss">
.quantity-operator {
  position: relative;
  &__actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    @media (max-width: $tablet) {
      display: inline-flex;
    }
    .disable-icon {
      color: var(--color-grey-2);
      cursor: default;
      &:hover {
        transform: none;
      }
    }
    svg {
      color: var(--color-pink-1);
      cursor: pointer;
      &:hover {
        transform: scale(1.1);
      }
    }
    // Only the +/- icon buttons may carry pink — the quantity readout itself
    // is foreground ink so pink stays a CTA-only colour in the modal.
    h3,
    input {
      margin: 0.5rem;
      width: 3rem;
      color: var(--color-grey-1);
      text-align: center;
      font-size: 1.5rem;
      font-weight: var(--weight-medium);
      border: none;
    }
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    input[type="number"] {
      -moz-appearance: textfield;
    }
    &--quantity {
      display: flex;
      width: auto !important;
      padding: 0.2rem 0.5rem;
      align-items: center;
    }
    .drop-quantity {
      background-color: var(--color-grey-3);
      border-radius: 0.4rem;
      svg {
        margin-left: 0.2rem;
      }
    }
  }
  &__picker {
    border-radius: 0.4rem;
    border: 2px solid var(--color-grey-3);
    padding: 1rem;
    background-color: var(--color-white-1);
    width: 5rem;
    margin-bottom: 1rem !important;
    z-index: 10000;
    @media (max-width: $tablet) {
      width: 5rem !important;
    }
    &--numbers {
      margin: 0;
      max-height: 12rem;
      overflow-y: auto;
      scrollbar-width: none;
      -ms-overflow-style: none;
      &::-webkit-scrollbar {
        display: none;
      }
      .slots {
        padding: 0.3rem;
        width: 90%;
        border-radius: 0.4rem;
        cursor: pointer;
        p {
          color: var(--color-grey-1);
          text-align: center;
          margin: 0;
        }
        &:hover {
          background-color: var(--color-pink-4);
          p {
            color: var(--color-pink-1);
          }
        }
      }
      .selected-time {
        background-color: var(--color-pink-1);
        p {
          color: var(--color-white-1);
        }
      }
    }
  }
}
</style>
